import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAccess } from '../../contexts/AccessContext';
import { adminErrorMessage, formatDateTime } from './adminShared';

// Switch app modules on/off for everyone. Admins keep seeing switched-off modules.
const AdminModules = () => {
  const { supabase } = useSupabase();
  const { modules, refreshModules } = useAccess();
  const [confirm, setConfirm] = useState(null); // module being switched off
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const save = async (module, enabled) => {
    setSaving(module.key);
    setError('');
    const { data, error: updateError } = await supabase
      .from('app_modules')
      .update({ enabled })
      .eq('key', module.key)
      .select('key');
    setSaving('');
    setConfirm(null);
    if (updateError || !data?.length) {
      // No rows back = the update was refused by the database (not an admin)
      setError(updateError ? adminErrorMessage(updateError) : "The change wasn't saved: you may no longer be an admin.");
      return;
    }
    await refreshModules();
    setSnackbar(`${module.name} switched ${enabled ? 'on' : 'off'}`);
  };

  const toggle = (module) => {
    if (module.enabled) setConfirm(module); // switching off needs a confirmation
    else save(module, true);
  };

  if (!modules.length) {
    return (
      <Alert severity="info">
        Module settings aren't set up yet. Run database/2026-11-03_admin.sql in the Supabase SQL Editor; until then every module stays on.
      </Alert>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper data-tour="admin-modules" sx={{ overflow: 'hidden' }}>
        {modules.map((m, index) => (
          <Box
            key={m.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 1.5, sm: 2.5 }, py: 1.75, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>{m.name}</Typography>
                <Chip size="small" label={m.enabled ? 'On' : 'Off'} color={m.enabled ? 'success' : 'default'} variant={m.enabled ? 'outlined' : 'filled'} />
              </Box>
              <Typography variant="body2" color="text.secondary">{m.description}</Typography>
              {m.updated_at && (
                <Typography variant="caption" color="text.disabled">Last changed {formatDateTime(m.updated_at)}</Typography>
              )}
            </Box>
            <Switch
              checked={m.enabled}
              disabled={saving === m.key}
              onChange={() => toggle(m)}
              inputProps={{ 'aria-label': `${m.name} ${m.enabled ? 'on' : 'off'}` }}
            />
          </Box>
        ))}
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Dashboard, Profile and Admin can't be switched off. Switching a module off hides it from the menu and the Dashboard; its pages show a notice. No data is deleted.
      </Typography>

      <Dialog open={Boolean(confirm)} onClose={() => !saving && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch off {confirm?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Users won't see {confirm?.name} in the menu or on the Dashboard, and its pages will say it isn't available. Their data stays as it is and comes back when you switch it on again.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>You'll still see it, marked "Off".</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={Boolean(saving)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => save(confirm, false)} disabled={Boolean(saving)}>
            {saving ? 'Saving…' : 'Switch off'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnackbar('')}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminModules;
